import { Checkbox, FormControlLabel } from "@mui/material";
import Box from "@mui/material/Box";
import { GridColDef, GridRowsProp, } from "@mui/x-data-grid";
import { observer } from "mobx-react-lite";
import * as React from 'react';
import { useEffect } from 'react';
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { useApiCall } from "../../../../hooks/useApiCall";
import i18nStore from "../../../../i18n/store";
import filterStore from "../../../CalculationsPage/components/filters/filtersStore";
import {
    createProjectBranch,
    deleteProjectBranch,
    deleteUnrelatedBranches,
    getProjectBranches,
    updateProjectBranch
} from "../../api/branches";
import { getProjectNodes } from "../../api/nodes";
import { BranchInterface } from "../../schemas/branches";
import { NodeInterface } from "../../schemas/nodes";

import BaseTable, { getHandlerDeleteClick, getHandlerDuplicateClick, TablePropsInterface } from "../BaseTable";
import DeleteUnattachedRowsButton from "../DeleteUnattachedRowsButton";
import { compareById } from "../utils";

import { useMuiBranchesColDefs } from "./colDefs";


const BranchesTable = observer((props: TablePropsInterface) => {
    const translations = i18nStore.getTranslations;
    const params = useParams();
    const projectId: number = +(params.projectId || 0);

    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const [showZeroSeqParamsFlag, setShowZeroSeqParamsFlag] = React.useState(false);

    const getNodesApi = useApiCall<NodeInterface[]>(() => getProjectNodes(projectId), []);
    useEffect(() => {
        if (projectId) {
            getNodesApi.apiCaller()?.then((nodes: any) => {
                filterStore._setNodes(nodes);
                return nodes;
            });

        }
    }, [projectId]);

    const handlerDuplicateClick: any = getHandlerDuplicateClick(
        projectId,
        rows,
        setRows,
        createProjectBranch,
    );

    const handlerDeleteClick: any = getHandlerDeleteClick(
        projectId,
        rows,
        setRows,
        deleteProjectBranch,
    );
    const colDefs: GridColDef[] = useMuiBranchesColDefs(
        rows,
        setRows,
        showZeroSeqParamsFlag,
        handlerDeleteClick,
        handlerDuplicateClick,
        updateProjectBranch,
    );

    const getBranchesApi = useApiCall<BranchInterface[]>(
        () => getProjectBranches(projectId).then(branches => {
            setRows(branches.sort(compareById).reverse());
            return branches;
        }),
        [],
    );

    const onDeleteUnrelatedRowsBtnHandler = () => {
        deleteUnrelatedBranches(projectId).then((deleted_rows: number[]) => {
            const deletedRowsSet = new Set(deleted_rows);
            setRows(prevRows => prevRows.filter((row) => !deletedRowsSet.has(row.id) && row.start_node_id && row.end_node_id));
        })
    }

    return (
        <BaseTable
            colDefs={colDefs}
            columnGroupingModel={[
                {
                    groupId: translations.zeroSequenceParameters,
                    description: translations.zeroSequenceParameters,
                    children: [
                        {field: 'is_active_0'},
                        {field: 'R0'},
                        {field: 'X0'},
                        {field: 'B0'},
                        {field: 'G0'},
                    ],
                },
                {
                    groupId: translations.nonZeroSequenceParameters,
                    description: translations.nonZeroSequenceParameters,
                    children: [
                        {field: 'R1'},
                        {field: 'X1'},
                        {field: 'B1'},
                        {field: 'G1'},

                    ],
                },
                {
                    groupId: translations.transformation_ratio,  // Transformation ratio
                    description: translations.transformation_ratio,
                    children: [
                        {field: 'start_node_u_nom'},
                        {field: 'end_node_u_nom'},
                        {field: 'transformer_angle'},
                    ],
                },
            ]}
            rows={rows}
            setRows={setRows}
            getTableApi={getBranchesApi}
            tableName={props.tableName}
            tableTitle={translations.branches}
            createApiFunc={createProjectBranch}
            updateApiFunc={updateProjectBranch}
            getRowClassname={(params) => {
                if (!params.row.start_node_id || !params.row.end_node_id) {
                    return "WarningFilled";
                }
                return "";
            }}
            createEmptyRow={() => {
                return {
                    id: uuidv4(),
                    name: '',
                    is_active: true,
                };
            }}
            extraToolbar={
                <Box sx={{flexGrow: 1, display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <FormControlLabel
                        value="end"
                        control={
                            <Checkbox
                                checked={showZeroSeqParamsFlag}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setShowZeroSeqParamsFlag(event.target.checked)}
                            />
                        }
                        label={translations.zeroSequenceParameters}
                        labelPlacement="end"
                    />
                    <DeleteUnattachedRowsButton
                        onClick={onDeleteUnrelatedRowsBtnHandler}
                        disabled={rows.find(row => !row.start_node_id || !row.end_node_id) === undefined}
                    />
                </Box>
            }
        />
    );
});

export default BranchesTable;
